import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useBlocker, useNavigate, useParams } from 'react-router-dom';
import {
  DndContext, DragOverlay, MouseSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors,
  pointerWithin, rectIntersection,
  type CollisionDetection, type DragEndEvent, type DragStartEvent, type Modifier,
} from '@dnd-kit/core';
import SiteHeader from '../components/SiteHeader';
import CardImage from '../components/CardImage';
import CardPreview from '../components/CardPreview';
import CardFilterBar, { type FilterValue, EMPTY_FILTERS } from '../components/CardFilterBar';
import { ConfirmDialog } from '../components/Modals';
import { decksApi } from '../api/decksApi';
import { cardsApi } from '../api/cardsApi';
import { useAuth } from '../auth/AuthContext';
import { useCardImage, primeImage, primeMeta, getMeta, ensureMeta, getFullCard } from '../api/cardCache';
import { setUnsavedGuard } from '../unsavedGuard';
import { computeLegality, type Legality } from '../deckLegality';
import { banStatus, BAN_BADGE } from '../banlist';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { cardImageUrl } from '../config';
import type { Card, DeckCard, DeckPrivacy, Filters, SynergyEntry } from '../../../shared/types';

type ZoneKey = 'main' | 'extra' | 'side' | 'considering';
type Target = ZoneKey | 'skill';
interface Zones { main: DeckCard[]; extra: DeckCard[]; side: DeckCard[]; considering: DeckCard[]; }

const ZONE_META: { key: ZoneKey; label: string; range?: [number, number] }[] = [
  { key: 'main', label: 'Main Deck', range: [20, 30] },
  { key: 'extra', label: 'Extra Deck', range: [0, 6] },
  { key: 'side', label: 'Side Deck', range: [0, 6] },
  { key: 'considering', label: 'Considering' },
];
const total = (list: DeckCard[]) => list.reduce((s, c) => s + c.count, 0);
const ZONE_KEYS: ZoneKey[] = ['main', 'extra', 'side', 'considering'];

// Prefer a card slot (for reordering) only when the pointer is genuinely inside
// one; otherwise fall back to normal zone hit-testing so drops outside any zone
// stay no-ops and cross-zone drops keep working as before.
/** Centre the drag overlay on the cursor (used for the big popup-card drag, which
 *  is grabbed anywhere on a large image). Uses the overlay's own size, not the
 *  source's, so it centres correctly even when they differ. */
const centerOverlayOnCursor: Modifier = ({ activatorEvent, draggingNodeRect, overlayNodeRect, transform }) => {
  const overlay = overlayNodeRect ?? draggingNodeRect;
  if (!activatorEvent || !draggingNodeRect || !overlay) return transform;
  const ev = activatorEvent as MouseEvent & TouchEvent;
  const x = ev.touches?.[0]?.clientX ?? (ev as MouseEvent).clientX ?? 0;
  const y = ev.touches?.[0]?.clientY ?? (ev as MouseEvent).clientY ?? 0;
  return {
    ...transform,
    x: transform.x + (x - draggingNodeRect.left) - overlay.width / 2,
    y: transform.y + (y - draggingNodeRect.top) - overlay.height / 2,
  };
};

const collisionDetection: CollisionDetection = args => {
  const within = pointerWithin(args);
  const collisions = within.length ? within : rectIntersection(args);
  const slot = collisions.find(c => String(c.id).startsWith('slot:'));
  return slot ? [slot] : collisions;
};

/** Sort bucket for "by type": Normal, then Effect, then Ritual monsters, then
 *  Spell, then Trap. Within a bucket the caller sorts alphabetically. */
function typeRank(name: string): number {
  const m = getMeta(name);
  if (!m) return 90;                       // meta not loaded yet -> sort last
  if (m.category === 'Spell') return 3;
  if (m.category === 'Trap') return 4;
  if (m.category === 'Monster') {
    const t = m.types ?? [];
    if (t.includes('Normal')) return 0;
    if (t.includes('Ritual')) return 2;
    return 1;                              // effect / other main-deck monster
  }
  return 5;                                // skill / unknown
}

/** Which zones a card may legally go in, from its category/types.
 *  Skill -> only Skill; Fusion -> only Extra; else Main/Side. Considering = any. */
function allowedZones(name: string): Set<Target> {
  const m = getMeta(name);
  if (!m) return new Set<Target>(['skill', 'main', 'extra', 'side', 'considering']); // unknown until meta loads
  if (m.category === 'Skill') return new Set<Target>(['skill', 'considering']);
  if (m.category === 'Monster' && m.types?.includes('Fusion')) return new Set<Target>(['extra', 'considering']);
  return new Set<Target>(['main', 'side', 'considering']);
}

const addTo = (list: DeckCard[], name: string): DeckCard[] => {
  const i = list.findIndex(c => c.name === name);
  return i >= 0 ? list.map((c, j) => (j === i ? { ...c, count: c.count + 1 } : c)) : [...list, { name, count: 1 }];
};
const removeOne = (list: DeckCard[], name: string): DeckCard[] => {
  const i = list.findIndex(c => c.name === name);
  if (i < 0) return list;
  return list[i].count <= 1 ? list.filter((_, j) => j !== i) : list.map((c, j) => (j === i ? { ...c, count: c.count - 1 } : c));
};

interface PreviewCtl { onHover: (n: string) => void; onLeave: () => void; onTap: (n: string) => void; }

/** A draggable card tile (used in zones + add results). Hover previews, tap opens preview. */
function DraggableCard({ name, from, image, count, controls, preview, problem }: {
  name: string; from: Target | 'search' | 'synergy'; image?: string | null;
  count?: number; controls?: { onAdd?: () => void; onRemove?: () => void }; preview: PreviewCtl; problem?: boolean;
}) {
  const dndId = `${from}:${name}`;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: dndId, data: { name, from } });
  // Cards already in a zone are also drop targets, so dropping one card onto
  // another reorders within the zone.
  const sortZone = (ZONE_KEYS as string[]).includes(from) ? (from as ZoneKey) : null;
  const { setNodeRef: setDropRef, isOver: slotOver } = useDroppable({
    id: sortZone ? `slot:${sortZone}:${name}` : `nodrop:${from}:${name}`,
    disabled: !sortZone,
  });
  const resolved = useCardImage(image === undefined ? name : null);
  const src = cardImageUrl(image !== undefined ? image : resolved);
  const ban = banStatus(name);
  return (
    <div
      ref={sortZone ? setDropRef : undefined}
      className={`dcard${isDragging ? ' dcard-dragging' : ''}${problem ? ' dcard-problem' : ''}${slotOver && sortZone ? ' dcard-slot-over' : ''}`}
    >
      <div
        ref={setNodeRef}
        className="dcard-img"
        {...listeners}
        {...attributes}
        onMouseEnter={() => preview.onHover(name)}
        onMouseLeave={preview.onLeave}
        onClick={() => preview.onTap(name)}
        title={name}
      >
        <CardImage src={src} alt={name} />
        {ban && <span className={`ban-badge ${BAN_BADGE[ban].cls}`} title={BAN_BADGE[ban].title}>{BAN_BADGE[ban].symbol}</span>}
        {count != null && count > 1 && <span className="dcard-count">×{count}</span>}
      </div>
      {controls && (
        <div className="dcard-ctrl">
          {controls.onAdd && <button type="button" onClick={controls.onAdd} aria-label="add one">+</button>}
          {count != null && <span className="dcard-n">{count}</span>}
          {controls.onRemove && <button type="button" onClick={controls.onRemove} aria-label="remove one">−</button>}
        </div>
      )}
    </div>
  );
}

function DropZone({ id, valid, className, children }: { id: Target; valid: boolean | null; className?: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const cls = valid === false ? ' zone-invalid' : valid === true ? ' zone-valid' : '';
  return <div ref={setNodeRef} className={`zone-drop${cls}${isOver && valid !== false ? ' zone-over' : ''}${className ? ' ' + className : ''}`}>{children}</div>;
}

export default function DeckBuilderPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { user, loading: authLoading, getToken, refresh } = useAuth();
  const navigate = useNavigate();

  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [ownerName, setOwnerName] = useState('');
  const [loadError, setLoadError] = useState('');
  const [loaded, setLoaded] = useState(false);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [privacy, setPrivacy] = useState<DeckPrivacy>('private');
  const [skill, setSkill] = useState<string | null>(null);
  const [zones, setZones] = useState<Zones>({ main: [], extra: [], side: [], considering: [] });
  const [coverCard, setCoverCard] = useState<string | null>(null);
  const [statusOpen, setStatusOpen] = useState(false);

  const [dirty, setDirty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [tab, setTab] = useState<'search' | 'synergy'>('search');
  const [copied, setCopied] = useState(false);
  const [copying, setCopying] = useState(false);

  // preview + drag
  // hovered = the persistent desktop preview panel (keeps the last card so you
  // can move the cursor over to scroll it); tapped = the mobile/click modal.
  const [hovered, setHovered] = useState<string | null>(null);
  // The click/tap preview modal keeps a small back/forward history (following
  // synergy links); it resets each time the modal is opened, not persisted.
  const [pvHist, setPvHist] = useState<{ stack: string[]; idx: number } | null>(null);
  const tapped = pvHist ? pvHist.stack[pvHist.idx] : null;   // current card, null = closed
  const openPreview = (name: string) => setPvHist({ stack: [name], idx: 0 });
  const navPreview = (name: string) => setPvHist(p => (p ? { stack: [...p.stack.slice(0, p.idx + 1), name], idx: p.idx + 1 } : { stack: [name], idx: 0 }));
  const backPreview = () => setPvHist(p => (p && p.idx > 0 ? { ...p, idx: p.idx - 1 } : p));
  const fwdPreview = () => setPvHist(p => (p && p.idx < p.stack.length - 1 ? { ...p, idx: p.idx + 1 } : p));
  const closePreview = () => setPvHist(null);
  const [active, setActive] = useState<{ name: string; from: string } | null>(null);
  const [activeAllowed, setActiveAllowed] = useState<Set<Target> | null>(null);

  const isOwner = !!user && ownerId === user.userId;
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  useEffect(() => {
    if (authLoading) return;             // wait for auth so private-deck owners send their token
    let cancelled = false;
    setLoaded(false); setLoadError('');
    decksApi.get(id, getToken, refresh)
      .then(d => {
        if (cancelled) return;
        setOwnerId(d.ownerId); setOwnerName(d.ownerName);
        setTitle(d.title); setPrivacy(d.privacy); setSkill(d.skill); setCoverCard(d.coverCard); setSlug(d.slug);
        setZones({ main: d.mainDeck, extra: d.extraDeck, side: d.sideDeck, considering: d.considering });
        setDirty(false); setLoaded(true);
        // prime meta for legality (zone-type checks) of loaded cards, then recompute
        const names = [d.skill, ...d.mainDeck, ...d.extraDeck, ...d.sideDeck, ...d.considering]
          .map(c => (typeof c === 'string' ? c : c?.name)).filter(Boolean) as string[];
        Promise.all(names.map(n => ensureMeta(n))).then(() => { if (!cancelled) setMetaTick(t => t + 1); });
      })
      .catch(e => { if (!cancelled) { setLoadError(e instanceof Error ? e.message : 'Deck not found'); setLoaded(true); } });
    return () => { cancelled = true; };
  }, [id, authLoading, getToken, refresh]);

  // Finish a "Copy Deck" that was started while signed out: once the deck has
  // loaded and the (now signed-in) user is back on this page, run the copy.
  useEffect(() => {
    if (loaded && !loadError && user && sessionStorage.getItem('pendingCopyDeck') === id) {
      sessionStorage.removeItem('pendingCopyDeck');
      copyDeck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, loadError, user, id]);

  // Guard unsaved changes against a hard unload (refresh / close / external nav)…
  // One signal for all exit paths. Only the owner can save, so only they can
  // have "unsaved changes".
  const hasUnsaved = isOwner && dirty;
  // Publish it so the header's Sign Out (which doesn't navigate) can warn too.
  useEffect(() => {
    setUnsavedGuard(hasUnsaved);
    return () => setUnsavedGuard(false);
  }, [hasUnsaved]);
  // Guard a hard unload (refresh / close / external nav)…
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => { if (hasUnsaved) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [hasUnsaved]);
  // …and in-app navigation + the browser back button (data router).
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    hasUnsaved && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm('You have unsaved changes to this deck. Leave without saving?')) blocker.proceed();
    else blocker.reset();
  }, [blocker]);

  const mark = () => setDirty(true);
  const place = useCallback((t: Target, name: string) => {
    mark();
    if (t === 'skill') { setSkill(name); return; }
    setZones(z => ({ ...z, [t]: addTo(z[t], name) }));
  }, []);
  const removeCard = useCallback((t: Target, name: string) => {
    mark();
    if (t === 'skill') { setSkill(null); return; }
    setZones(z => ({ ...z, [t]: removeOne(z[t], name) }));
  }, []);
  // Drag-reorder: move `name`'s entry to where `overName` sits in the zone.
  const reorderInZone = useCallback((zone: ZoneKey, name: string, overName: string) => {
    mark();
    setZones(z => {
      const list = z[zone];
      const from = list.findIndex(c => c.name === name);
      const to = list.findIndex(c => c.name === overName);
      if (from < 0 || to < 0 || from === to) return z;
      const copy = [...list];
      const [item] = copy.splice(from, 1);
      copy.splice(to, 0, item);
      return { ...z, [zone]: copy };
    });
  }, []);
  // Auto-sort a zone: alphabetical, or by type (then alphabetical within type).
  const sortZoneBy = useCallback((zone: ZoneKey, mode: 'az' | 'type') => {
    mark();
    setZones(z => ({
      ...z,
      [zone]: [...z[zone]].sort((a, b) => {
        if (mode === 'type') { const r = typeRank(a.name) - typeRank(b.name); if (r) return r; }
        return a.name.localeCompare(b.name);
      }),
    }));
  }, []);

  function onDragStart(e: DragStartEvent) {
    const name = e.active.data.current?.name as string;
    const from = e.active.data.current?.from as string;
    document.body.classList.add('dnd-dragging'); // lock page scroll during the drag
    setActive({ name, from });
    if (from !== 'popup') closePreview();   // keep the popup open (it fades) when dragging from it
    ensureMeta(name).then(() => setActiveAllowed(allowedZones(name)));
    setActiveAllowed(allowedZones(name));
  }
  function onDragCancel() {
    document.body.classList.remove('dnd-dragging');
    setActive(null); setActiveAllowed(null);
  }
  function onDragEnd(e: DragEndEvent) {
    const name = e.active.data.current?.name as string;
    const from = e.active.data.current?.from as Target | 'search' | 'synergy' | 'popup';
    document.body.classList.remove('dnd-dragging');
    setActive(null); setActiveAllowed(null);
    const overId = e.over?.id as string | undefined;
    if (!overId) return;
    // Drop targets: a card slot ("slot:<zone>:<name>"), a zone (id = zone), or a
    // mobile zone tab (id = "tab-<zone>").
    let to: Target;
    let overName: string | null = null;
    if (overId.startsWith('slot:')) {
      const rest = overId.slice(5);
      const ci = rest.indexOf(':');
      to = rest.slice(0, ci) as Target;
      overName = rest.slice(ci + 1);
    } else if (overId.startsWith('tab-')) {
      to = overId.slice(4) as Target;
    } else {
      to = overId as Target;
    }
    if (!allowedZones(name).has(to)) return; // illegal zone — reject
    // Dropped onto another card in the same zone -> reorder.
    if (from === to && overName && overName !== name) { reorderInZone(to as ZoneKey, name, overName); return; }
    if (from === to) return;
    place(to, name);
    if (from !== 'search' && from !== 'synergy' && from !== 'popup') removeCard(from, name); // move between zones
  }

  // Cover card = the deck's icon. Options are the cards actually in the deck; it
  // defaults to the first Main/Extra/Skill card, but a manual pick sticks until
  // that card leaves the deck, at which point it falls back to the default.
  const deckCardNames = (() => {
    const s: string[] = [];
    if (skill) s.push(skill);
    zones.main.forEach(c => s.push(c.name));
    zones.extra.forEach(c => s.push(c.name));
    zones.side.forEach(c => s.push(c.name));
    return [...new Set(s)];
  })();
  const defaultCover = zones.main[0]?.name ?? zones.extra[0]?.name ?? skill ?? null;
  const effectiveCover = coverCard && deckCardNames.includes(coverCard) ? coverCard : defaultCover;

  async function save() {
    setSaving(true); setSaveMsg('');
    try {
      await decksApi.update(id, {
        title: title.trim() || 'Untitled Deck', privacy, skill,
        mainDeck: zones.main, extraDeck: zones.extra, sideDeck: zones.side, considering: zones.considering,
        coverCard: effectiveCover,
      }, getToken, refresh);
      setDirty(false); setSaveMsg('Saved'); setTimeout(() => setSaveMsg(''), 2000);
    } catch (e) { setSaveMsg(e instanceof Error ? e.message : 'Save failed'); } finally { setSaving(false); }
  }
  function copyLink() { navigator.clipboard?.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 1500); }
  // Duplicate this deck into the signed-in user's own decks (works on anyone's
  // deck, including your own). Signed-out users are sent to sign in first, then
  // the copy runs automatically when they land back here (see the effect below).
  async function copyDeck() {
    if (!user) {
      sessionStorage.setItem('pendingCopyDeck', id);
      navigate('/login', { state: { from: `/deck/${id}${slug ? `/${slug}` : ''}` } });
      return;
    }
    setCopying(true); setSaveMsg('');
    try {
      const created = await decksApi.create({
        title: `${title.trim() || 'Untitled Deck'} - Copy`,
        privacy: 'private', skill,
        mainDeck: zones.main, extraDeck: zones.extra, sideDeck: zones.side, considering: zones.considering,
        coverCard: effectiveCover,
      }, getToken, refresh);
      navigate(`/deck/${created.id}/${created.slug}`);
    } catch (e) { setSaveMsg(e instanceof Error ? e.message : 'Copy failed'); }
    finally { setCopying(false); }
  }
  async function deleteDeck() {
    setConfirmDelete(false);
    try { await decksApi.remove(id, getToken, refresh); setDirty(false); navigate('/decks'); }
    catch (e) { setSaveMsg(e instanceof Error ? e.message : 'Delete failed'); }
  }

  // Legality (banlist point-pools, 3-copy rule, zone sizes/types). metaTick
  // forces a recompute once card metadata (for zone-type checks) has loaded.
  const [metaTick, setMetaTick] = useState(0);
  const legality = useMemo(
    () => computeLegality(skill, zones, getMeta),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [skill, zones, metaTick],
  );

  const previewCtl: PreviewCtl = {
    onHover: n => setHovered(n),
    onLeave: () => {},          // persist the last hovered card so it stays readable
    onTap: openPreview,
  };

  const deckNames = [...(skill ? [skill] : []), ...zones.main, ...zones.extra, ...zones.side].map(c => (typeof c === 'string' ? c : c.name));
  const zoneValid = (z: Target): boolean | null => (activeAllowed ? activeAllowed.has(z) : null);

  const ZONE = (k: ZoneKey) => ZONE_META.find(z => z.key === k)!;
  const renderZone = (z: (typeof ZONE_META)[number], colClass?: string) => {
    const t = total(zones[z.key]);
    return (
      <DropZone key={z.key} id={z.key} valid={zoneValid(z.key)} className={colClass}>
        <section className={`deck-zone deck-zone--${legality.zone[z.key]}`} id={`zone-${z.key}`}>
          <div className="deck-zone-head">
            <h3>{z.label} <span className={legality.zone[z.key] === 'ok' ? 'muted' : 'zone-count-bad'}>({t}{z.range ? `/${z.range[1]}` : ''})</span></h3>
            {isOwner && zones[z.key].length > 1 && (
              <select
                className="zone-sort"
                value=""
                title="Auto-sort this zone"
                onChange={e => { const v = e.target.value; if (v) sortZoneBy(z.key, v as 'az' | 'type'); e.currentTarget.blur(); }}
              >
                <option value="">Sort ▾</option>
                <option value="az">A–Z</option>
                {z.key !== 'extra' && <option value="type">By type</option>}
              </select>
            )}
          </div>
          <div className="zone-cards">
            {zones[z.key].length === 0 && <p className="muted zone-empty">Drag cards here</p>}
            {zones[z.key].map(c => (
              <DraggableCard key={c.name} name={c.name} from={z.key} count={c.count} preview={previewCtl}
                problem={legality.problemCards.has(c.name)}
                controls={isOwner ? { onAdd: () => place(z.key, c.name), onRemove: () => removeCard(z.key, c.name) } : undefined} />
            ))}
          </div>
        </section>
      </DropZone>
    );
  };

  if (!loaded) return <div><SiteHeader /><main className="page page--wide"><p className="muted">Loading…</p></main></div>;
  if (loadError) return (
    <div><SiteHeader /><main className="page page--wide">
      <h1>Deck not found</h1><p className="error">{loadError}</p><p><Link to="/decks">← Decks</Link></p>
    </main></div>
  );

  return (
    <div>
      <SiteHeader />
      <DndContext sensors={sensors} collisionDetection={collisionDetection} autoScroll={false} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
        <main className="page page--builder">
          {/* Header */}
          <div className="builder-head">
            {isOwner
              ? <input className="builder-title" value={title} onChange={e => { setTitle(e.target.value); mark(); }} placeholder="Deck title" />
              : <h1 className="builder-title-h">{title}</h1>}
            {isOwner && deckCardNames.length > 0 && (
              <label className="cover-select" title="Card shown as this deck's icon">
                <span>Icon</span>
                <select value={effectiveCover ?? ''} onChange={e => { setCoverCard(e.target.value || null); mark(); }}>
                  {deckCardNames.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
            )}
            <div className="builder-head-actions">
              <StatusBadge legality={legality} open={statusOpen} onToggle={() => setStatusOpen(o => !o)} />
              {isOwner && (
                <select value={privacy} onChange={e => { setPrivacy(e.target.value as DeckPrivacy); mark(); }} aria-label="Privacy">
                  <option value="private">Private</option>
                  <option value="friends">Friends only</option>
                  <option value="unlisted">Unlisted</option>
                  <option value="public">Public</option>
                </select>
              )}
              <Link to={`/playtest/${id}${slug ? `/${slug}` : ''}`} className="link-btn2 playtest-btn">Playtest</Link>
              <button type="button" onClick={copyLink} className="link-btn2">{copied ? 'Copied!' : 'Share link'}</button>
              <button type="button" onClick={copyDeck} disabled={copying} className="link-btn2">{copying ? 'Copying…' : 'Copy Deck'}</button>
              {isOwner && <button type="button" onClick={save} disabled={saving || !dirty} className="save-btn">{saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}</button>}
              {isOwner && <button type="button" onClick={() => setConfirmDelete(true)} className="btn-danger">Delete</button>}
            </div>
          </div>
          <p className="muted" style={{ marginTop: 0 }}>by {ownerName || 'unknown'} · {privacy.charAt(0).toUpperCase() + privacy.slice(1)}{dirty ? ' · unsaved changes' : ''}{saveMsg && ` · ${saveMsg}`}</p>

          {statusOpen && <StatusDetails legality={legality} />}

          <div className={`builder-grid${isOwner ? '' : ' builder-grid--view'}`}>
            {/* Zones */}
            <div className="builder-zones">
              {/* Skill (1 card) shares a row with the Extra deck (≤6) to reclaim
                  the Skill zone's wasted width and reduce scrolling. */}
              <div className="zone-row">
                <DropZone id="skill" valid={zoneValid('skill')} className="zone-col-skill">
                  <section className={`deck-zone deck-zone--${legality.zone.skill}`} id="zone-skill">
                    <h3>Skill {skill ? '' : <span className="muted">— drag a Skill card here</span>}</h3>
                    <div className="zone-cards">
                      {skill && <DraggableCard name={skill} from="skill" preview={previewCtl} problem={legality.problemCards.has(skill)} controls={isOwner ? { onRemove: () => removeCard('skill', skill) } : undefined} />}
                    </div>
                  </section>
                </DropZone>
                {renderZone(ZONE('extra'), 'zone-col-extra')}
              </div>
              {renderZone(ZONE('main'))}
              {renderZone(ZONE('side'))}
              {renderZone(ZONE('considering'))}
            </div>

            {/* Mobile drop-bar — between the zones and the card search; pins to
                the top while scrolling the card list. */}
            {isOwner && <ZoneDropBar skill={skill} zones={zones} activeAllowed={activeAllowed} />}

            {/* Add panel (owner only) */}
            {isOwner && (
              <div className="builder-add">
                <p className="drag-hint">Drag a card into a zone to add it. Hover or tap a card to read it.</p>
                <div className="add-tabs">
                  <button type="button" className={tab === 'search' ? 'active' : ''} onClick={() => setTab('search')}>Search</button>
                  <button type="button" className={tab === 'synergy' ? 'active' : ''} onClick={() => setTab('synergy')}>Synergy</button>
                </div>
                {tab === 'search'
                  ? <SearchPanel preview={previewCtl} />
                  : <SynergyPanel deckNames={deckNames} preview={previewCtl} />}
              </div>
            )}

            {/* Card preview — its own column so hovering never reflows the cards */}
            {isOwner && (
              <aside className="preview-col">
                <div className="preview-panel">
                  <CardPreview name={hovered} />
                </div>
              </aside>
            )}
          </div>
        </main>

        <DragOverlay dropAnimation={null} modifiers={active?.from === 'popup' ? [centerOverlayOnCursor] : undefined}>
          {active ? <div className="drag-overlay"><OverlayImg name={active.name} /></div> : null}
        </DragOverlay>

        {/* Tap preview modal (mobile / click). Inside the DndContext so its card
            can be dragged into a zone; it fades out while that drag is in flight. */}
        {tapped && pvHist && (
          <div className={`preview-modal-backdrop${active?.from === 'popup' ? ' dragging-out' : ''}`} onClick={closePreview}>
            <div className="preview-modal" onClick={e => e.stopPropagation()}>
              <div className="preview-nav">
                <button type="button" className="preview-navbtn" onClick={backPreview} disabled={pvHist.idx === 0} title="Back" aria-label="Back">‹</button>
                <button type="button" className="preview-navbtn" onClick={fwdPreview} disabled={pvHist.idx >= pvHist.stack.length - 1} title="Forward" aria-label="Forward">›</button>
                <button type="button" className="preview-navbtn" onClick={closePreview} title="Close" aria-label="Close">✕</button>
              </div>
              <CardPreview name={tapped} showSynergy onPickSynergy={navPreview} draggableCard />
            </div>
          </div>
        )}
      </DndContext>

      {confirmDelete && (
        <ConfirmDialog title="Delete this deck?" message="This cannot be undone." confirmLabel="Delete" danger
          onConfirm={deleteDeck} onCancel={() => setConfirmDelete(false)} />
      )}
    </div>
  );
}

/** Image used inside the DragOverlay (resolves by name). */
function OverlayImg({ name }: { name: string }) {
  const img = useCardImage(name);
  return <CardImage src={cardImageUrl(img)} alt={name} />;
}

/** Legal / Draft / Illegal pill — sits in the title row; toggles the details. */
function StatusBadge({ legality, open, onToggle }: { legality: Legality; open: boolean; onToggle: () => void }) {
  const { status, issues, drafts } = legality;
  const count = issues.length + drafts.length;
  const cls = status === 'Legal' ? 'legal' : status === 'Draft' ? 'draft' : 'illegal';
  return (
    <button
      type="button"
      className={`status-badge status-${cls}${open ? ' status-badge-open' : ''}`}
      onClick={onToggle}
      disabled={count === 0}
      title={count ? 'Show issues' : undefined}
    >
      <span className="status-dot" /> {status}
      {count > 0 && <span className="status-count">{count}</span>}
    </button>
  );
}

/** The expandable list of legality issues, shown as its own row when opened. */
function StatusDetails({ legality }: { legality: Legality }) {
  const { issues, drafts } = legality;
  if (issues.length + drafts.length === 0) return null;
  return (
    <div className="status-details status-details-row">
      {issues.map((s, i) => <div key={`i${i}`} className="status-note status-note-bad">✕ {s}</div>)}
      {drafts.map((s, i) => <div key={`d${i}`} className="status-note status-note-warn">• {s}</div>)}
    </div>
  );
}

/** A single zone tab in the mobile drop-bar — a drop target + scroll shortcut. */
function ZoneTab({ zone, label, count, valid, onScroll }: {
  zone: Target; label: string; count: number; valid: boolean | null; onScroll: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `tab-${zone}` });
  const cls = valid === false ? ' ztab-invalid' : valid === true ? ' ztab-valid' : '';
  return (
    <button type="button" ref={setNodeRef} className={`ztab${cls}${isOver && valid !== false ? ' ztab-over' : ''}`} onClick={onScroll}>
      {label}<span className="ztab-n">{count}</span>
    </button>
  );
}

/** Mobile bar of zone drop targets. It's *always* portaled to <body> and fixed
 *  off-screen at the top; a scroll listener just fades it in once you've scrolled
 *  past its slot (between the zones and the card search). Because it never enters
 *  the page flow, there's no reflow/stutter — only its own opacity changes. */
function ZoneDropBar({ skill, zones, activeAllowed }: { skill: string | null; zones: Zones; activeAllowed: Set<Target> | null }) {
  const scrollTo = (z: string) => document.getElementById(`zone-${z}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const valid = (z: Target) => (activeAllowed ? activeAllowed.has(z) : null);
  const [visible, setVisible] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => {
      if (window.innerWidth > 1080) { setVisible(false); return; } // desktop: never show
      const s = sentinelRef.current;
      if (!s) return;
      const show = s.getBoundingClientRect().top <= 0;
      setVisible(prev => (prev === show ? prev : show));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); };
  }, []);

  const tabs = (
    <>
      <ZoneTab zone="skill" label="Skill" count={skill ? 1 : 0} valid={valid('skill')} onScroll={() => scrollTo('skill')} />
      <ZoneTab zone="main" label="Main" count={total(zones.main)} valid={valid('main')} onScroll={() => scrollTo('main')} />
      <ZoneTab zone="extra" label="Extra" count={total(zones.extra)} valid={valid('extra')} onScroll={() => scrollTo('extra')} />
      <ZoneTab zone="side" label="Side" count={total(zones.side)} valid={valid('side')} onScroll={() => scrollTo('side')} />
      <ZoneTab zone="considering" label="Consider" count={total(zones.considering)} valid={valid('considering')} onScroll={() => scrollTo('considering')} />
    </>
  );

  return (
    <div className="zone-dropbar-wrap">
      <div ref={sentinelRef} className="zone-dropbar-sentinel" aria-hidden="true" />
      {createPortal(
        <div className={`zone-dropbar zone-dropbar-fixed${visible ? ' visible' : ''}`}>{tabs}</div>,
        document.body,
      )}
    </div>
  );
}

/** Built-in card search with the full filter bar; results are draggable and load
 *  a page at a time (infinite scroll) so all matches are reachable without
 *  pulling the whole catalog up front. */
const SEARCH_PAGE = 40;
function SearchPanel({ preview }: { preview: PreviewCtl }) {
  const [input, setInput] = useState('');
  const q = useDebouncedValue(input, 300);
  const [filters, setFilters] = useState<Filters | null>(null);
  const [fv, setFv] = useState<FilterValue>(EMPTY_FILTERS);
  const [rows, setRows] = useState<{ name: string; image: string | null }[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const reqRef = useRef(0);       // discards responses from a superseded query
  const busyRef = useRef(false);  // serializes overlapping page loads

  useEffect(() => { cardsApi.filters().then(setFilters).catch(() => {}); }, []);

  const toRow = (c: { name: string; primaryImage: string | null }) => ({ name: c.name, image: c.primaryImage });
  const fvKey = JSON.stringify(fv);

  // New query/filters → reset to page 1.
  useEffect(() => {
    const rid = ++reqRef.current;
    busyRef.current = true; setLoading(true);
    cardsApi.search({ search: q || undefined, ...fv, page: 1, pageSize: SEARCH_PAGE })
      .then(res => {
        if (reqRef.current !== rid) return;
        res.cards.forEach(c => { primeImage(c.name, c.primaryImage); primeMeta(c.name, c.category, c.types); });
        setRows(res.cards.map(toRow)); setPage(1); setTotalPages(res.totalPages);
      })
      .catch(() => { if (reqRef.current === rid) { setRows([]); setTotalPages(1); } })
      .finally(() => { if (reqRef.current === rid) { busyRef.current = false; setLoading(false); } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, fvKey]);

  const loadMore = useCallback(() => {
    if (busyRef.current || page >= totalPages) return;
    const rid = reqRef.current; const next = page + 1;
    busyRef.current = true; setLoading(true);
    cardsApi.search({ search: q || undefined, ...fv, page: next, pageSize: SEARCH_PAGE })
      .then(res => {
        if (reqRef.current !== rid) return;
        res.cards.forEach(c => { primeImage(c.name, c.primaryImage); primeMeta(c.name, c.category, c.types); });
        setRows(prev => [...prev, ...res.cards.map(toRow)]); setPage(next); setTotalPages(res.totalPages);
      })
      .catch(() => {})
      .finally(() => { if (reqRef.current === rid) { busyRef.current = false; setLoading(false); } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, totalPages, q, fvKey]);

  // Fetch the next page as the sentinel nears the viewport (re-observing after
  // each load also fills the panel when the first page is short).
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current; if (!el) return;
    const io = new IntersectionObserver(e => { if (e[0].isIntersecting) loadMore(); }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore]);

  return (
    <div>
      <div className="builder-searchrow">
        <input type="search" placeholder="Search cards…" value={input} onChange={e => setInput(e.target.value)} />
        <CardFilterBar filters={filters} value={fv}
          onChange={(k, v) => setFv(prev => ({ ...prev, [k]: v }))}
          onClear={() => setFv(EMPTY_FILTERS)} />
      </div>
      {loading && rows.length === 0 && <p className="muted">Loading…</p>}
      {!loading && rows.length === 0 && <p className="muted">No cards found.</p>}
      <div className="add-grid">
        {rows.map(r => <DraggableCard key={r.name} name={r.name} from="search" image={r.image} preview={preview} />)}
      </div>
      <div ref={sentinelRef} style={{ height: 1 }} />
      {loading && rows.length > 0 && <p className="muted search-more">Loading more…</p>}
    </div>
  );
}

/** Synergy suggestions aggregated from the deck's current cards; draggable. */
function SynergyPanel({ deckNames, preview }: { deckNames: string[]; preview: PreviewCtl }) {
  const [suggestions, setSuggestions] = useState<{ name: string; image: string | null; score: number; hits: number }[] | null>(null);
  const key = deckNames.join('|');

  useEffect(() => {
    let cancelled = false; setSuggestions(null);
    if (deckNames.length === 0) { setSuggestions([]); return; }
    Promise.all(deckNames.map(getFullCard)).then((cards: (Card | null)[]) => {
      if (cancelled) return;
      const inDeck = new Set(deckNames);
      // affinity = sum of each deck card's Jaccard synergy (0-100) with this
      // candidate. It rewards both strength and breadth (synergizing with many
      // of your cards), but is unbounded — so we normalize to 0-100 against the
      // strongest candidate for display, which keeps the ranking and caps the
      // top suggestion at 100 instead of jumping into the hundreds.
      const agg = new Map<string, { name: string; image: string | null; affinity: number; hits: number }>();
      const add = (e: SynergyEntry) => {
        if (inDeck.has(e.name)) return;
        const cur = agg.get(e.name) ?? { name: e.name, image: e.image, affinity: 0, hits: 0 };
        cur.affinity += e.score; cur.hits += 1; agg.set(e.name, cur);
      };
      for (const c of cards) {
        if (!c) continue;
        // Suggest both synergy partners and the character Skills the deck's cards
        // co-occur with, so Skills show up alongside the rest.
        c.topSynergy.forEach(add);
        c.topSkills.forEach(add);
      }
      const vals = [...agg.values()];
      const max = Math.max(1, ...vals.map(v => v.affinity));
      setSuggestions(
        vals
          .map(s => ({ name: s.name, image: s.image, hits: s.hits, score: Math.round(100 * s.affinity / max) }))
          .sort((a, b) => b.score - a.score)
          .slice(0, 40),
      );
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (deckNames.length === 0) return <p className="muted">Add some cards, then drag in synergy suggestions based on them.</p>;
  if (!suggestions) return <p className="muted">Computing synergy…</p>;
  if (suggestions.length === 0) return <p className="muted">No synergy suggestions yet.</p>;
  return (
    <div className="add-grid">
      {suggestions.map(s => (
        <div
          key={s.name}
          className="syn-suggest"
          title={`Synergizes with ${s.hits} of your card${s.hits === 1 ? '' : 's'} · 100 = strongest match for this deck`}
        >
          <DraggableCard name={s.name} from="synergy" image={s.image} preview={preview} />
          <span className="syn-card-score syn-suggest-score">{s.score}</span>
        </div>
      ))}
    </div>
  );
}
